import type { ListingDesignItem } from '../../domain/models/ProductListing';

export type PlpCell =
  | { type: 'product'; product: ListingDesignItem }
  | { type: 'banner'; id: string }
  | { type: 'explore-links' }
  | { type: 'store-location-widget'; id: string };

function shouldInsertMobileBannerAfterRow(rowIndex: number): boolean {
  if (rowIndex === 2) return true;
  if (rowIndex > 2 && (rowIndex - 2) % 10 === 0) return true;
  return false;
}

export function buildPlpCells(
  products: ListingDesignItem[],
  columnCount: 2 | 3,
  hasExploreLinks = false,
  hasStoreWidget = false,
  hasBanners = true,
): PlpCell[] {
  const cells: PlpCell[] = [];

  if (columnCount === 3) {
    // Desktop: first banner occupies the 3rd slot of row 1 (after 2 products).
    // Repeat every 30 products (10 full rows) thereafter.
    // Explore-links widget inserted as a full row between row 8 and row 9 (once only).
    // Store-location widget inserted as a full row after row 2 (after 5 products, cells.length===6).
    let bannerCount = 0;
    let exploreLinksInserted = false;
    let storeWidgetInserted = false;
    for (let i = 0; i < products.length; i++) {
      // Insert explore-links row when 8 complete rows (24 cells) have been pushed
      // and there is at least one more product to follow.
      if (hasExploreLinks && !exploreLinksInserted && cells.length === 24 && i + 1 < products.length) {
        cells.push({ type: 'explore-links' });
        cells.push({ type: 'explore-links' });
        cells.push({ type: 'explore-links' });
        exploreLinksInserted = true;
      }
      cells.push({ type: 'product', product: products[i] });
      const isNotLastProduct = i + 1 < products.length;
      if (!isNotLastProduct) break;
      if (hasBanners && (i === 1 || (i > 1 && (i - 1) % 30 === 0))) {
        cells.push({ type: 'banner', id: `b-${bannerCount++}` });
      }
      // Insert store-location widget after product index 4 (cells.length===6, row boundary).
      if (hasStoreWidget && !storeWidgetInserted && i === 4 && cells.length % columnCount === 0) {
        cells.push({ type: 'store-location-widget', id: 'slw-0' });
        cells.push({ type: 'store-location-widget', id: 'slw-1' });
        cells.push({ type: 'store-location-widget', id: 'slw-2' });
        storeWidgetInserted = true;
      }
    }
  } else {
    // Mobile: insert a full-row banner (two cells) after row 2 and every 10 rows.
    // Insert store-location widget as a full row after row 3 (after 6 products).
    let storeWidgetInserted = false;
    for (let i = 0; i < products.length; i++) {
      cells.push({ type: 'product', product: products[i] });
      const isEndOfRow = (i + 1) % columnCount === 0;
      const isNotLastProduct = i + 1 < products.length;
      if (!isEndOfRow || !isNotLastProduct) continue;
      const rowIndex = (i + 1) / columnCount;
      if (!Number.isInteger(rowIndex)) continue;
      if (hasBanners && shouldInsertMobileBannerAfterRow(rowIndex)) {
        const bannerId = `b-row-${rowIndex}`;
        cells.push({ type: 'banner', id: `${bannerId}-1` });
        cells.push({ type: 'banner', id: `${bannerId}-2` });
      }
      // Insert store-location widget after row 3 (after 6 products, cells.length % 2 === 0).
      if (hasStoreWidget && !storeWidgetInserted && rowIndex === 3) {
        cells.push({ type: 'store-location-widget', id: 'slw-0' });
        cells.push({ type: 'store-location-widget', id: 'slw-1' });
        storeWidgetInserted = true;
      }
    }
  }

  return cells;
}

/** Row index in the window virtualizer for the first cell matching designId (mobile PLP restore). */
export function findRowIndexForDesignId(
  cells: PlpCell[],
  columnCount: number,
  designId: number
): number | null {
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    if (c.type === 'product' && c.product.designId === designId) {
      return Math.floor(i / columnCount);
    }
  }
  return null;
}

/** Product row depth excluding banner rows (matches old-react scroll_depth). */
export function getPlpProductScrollDepth(
  cells: PlpCell[],
  columnCount: number,
  rowIndex: number
): number {
  let bannerCount = 0;
  for (let r = 0; r < rowIndex; r++) {
    const firstCell = cells[r * columnCount];
    if (firstCell?.type === 'banner') {
      bannerCount++;
    }
  }
  return rowIndex - bannerCount + 1;
}
